import { Modal } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  DEFAULT_FILTERS,
  DEFAULT_SORTING,
  TemplateBase,
  TemplatesDisplay,
  TemplatesDisplayProps,
} from "./TemplatesDisplay.js";
import { useTemplatesQueryVariablesInState } from "./useTemplatesQueryVariables.js";
import {
  searchQuotesSortingToPropertySortings,
  searchQuotesSortingToQuoteTemplatesSorting,
} from "../helpers.js";
import {
  TemplateSearchFiltersInput,
  TemplateSearchSortingInput,
} from "../../../../clients/graphqlTypes.js";

export const TemplateSelectModal = ({
  handleClose,
  handleComplete,
  title,
  initialFilters,
  initialSorting,
  fixedFilters,
}: Pick<TemplatesDisplayProps, "fixedFilters"> & {
  handleClose: () => void;
  handleComplete: (template: TemplateBase, handleClose: () => void) => void;
  title: string;
  initialFilters: TemplateSearchFiltersInput;
  initialSorting: TemplateSearchSortingInput[];
}) => {
  const { t } = useTranslate(["Templates", "Global"]);

  const [selectedTemplate, setSelectedTemplate] =
    React.useState<TemplateBase | null>(null);

  // Property sortings (a placeholder's sorting defaults) have no grid column,
  // so the column conversion below drops them; they are appended to the query
  // after the column sort instead.
  const seedPropertySortings = React.useMemo(
    () => searchQuotesSortingToPropertySortings(initialSorting),
    [initialSorting]
  );

  const {
    queryBaseVariables,
    filters,
    setFilters,
    sorting,
    setSorting,
    paginationModel,
    setPaginationModel,
    searchTerm,
    setSearchTerm,
  } = useTemplatesQueryVariablesInState({
    initialFilters: initialFilters ?? DEFAULT_FILTERS,
    initialSorting: searchQuotesSortingToQuoteTemplatesSorting(
      initialSorting ?? DEFAULT_SORTING
    ),
    initialSearchTerm: "",
  });

  // Clear to empty, not back to `initialFilters`: callers seed those with a
  // placeholder's search filter defaults, so resetting to them made "Clear all"
  // a no-op (MEIS-10975). The type and applicable-for scope live in
  // `fixedFilters`, which TemplatesDisplay merges in on top regardless.
  const resetFilters = React.useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, [setFilters]);

  return (
    <Modal
      title={title}
      maxWidth="xl"
      fixedHeight
      // Without this, the modal-stack registration effect makes it render
      // display:none on its first frame, which silently defeats MUI's
      // one-shot auto-focus — Escape then does nothing until the user
      // clicks something inside to focus it manually.
      notInStack
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Select", { ns: "Global" }),
          handleClick: () => {
            if (!selectedTemplate) {
              throw new Error("Template missing");
            }
            handleComplete(selectedTemplate, handleClose);
          },
          buttonProps: {
            disabled: !selectedTemplate,
          },
        },
      ]}
      handleClose={handleClose}
    >
      <TemplatesDisplay
        fixedFilters={fixedFilters}
        queryBaseVariables={queryBaseVariables}
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
        sorting={sorting}
        setSorting={setSorting}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        seedPropertySortings={seedPropertySortings}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        onInspectedTemplateClick={template => {
          handleComplete(template, handleClose);
        }}
      />
    </Modal>
  );
};
